import { create } from "zustand";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { config } from "@/lib/config";

export type Installation = {
  id: string;
  workspace: string;
  team: string;
  installedAt: string;
};

export type RotatedKeys = {
  installationId: string;
  team: string;
  apiKey: string;
  callbackKey: string;
};

type InstallationData = {
  installations: Installation[];
  loading: boolean;
  initialized: boolean;
  fetch: (userId: string) => Promise<void>;
};

type RotateTokensData = {
  rotatingId: string | null;
  rotateKeys: (installationId: string, team: string) => Promise<RotatedKeys | null>;
};

type RemoveInstallationData = {
  removingId: string | null;
  remove: (id: string) => Promise<void>;
};

type Store = InstallationData & RotateTokensData & RemoveInstallationData;

export const useInstallations = create<Store>((set, get) => ({
  installations: [],
  loading: false,
  initialized: false,
  rotatingId: null,
  removingId: null,

  fetch: async (_userId: string) => {
    set({ loading: true });
    const { data, error } = await supabase
      .from("slack_installations")
      .select("id, team_id, team_name, created_at")
      .order("created_at", { ascending: false });

    if (error) {
      set({ loading: false, initialized: true });
      toast.error("Failed to load installations");
      return;
    }

    const installations: Installation[] = (data ?? []).map((row) => ({
      id: row.id as string,
      workspace: (row.team_name as string) ?? row.team_id,
      team: row.team_id as string,
      installedAt: (row.created_at as string).slice(0, 10),
    }));

    set({ installations, loading: false, initialized: true });
  },

  remove: async (id: string) => {
    set({ removingId: id });
    const { error } = await supabase.from("slack_installations").delete().eq("id", id).select();

    if (error) {
      toast.error("Failed to remove installation");
      return;
    }

    set((s) => {
      const remaining = s.installations.filter((x) => x.id !== id);
      return {
        removingId: null,
        installations: remaining,
      };
    });

    toast.success("Installation removed");
  },

  rotateKeys: async (installationId: string, team: string) => {
    set({ rotatingId: installationId });
    try {
      const res = await fetch(
        `${config.apiBaseUrl}/slack_installations/${installationId}/rotate-keys`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Authorization: "cookie",
          },
        },
      );
      const json = (await res.json()) as { ok: boolean; apiKey?: string; callbackKey?: string };
      if (json.ok && json.apiKey && json.callbackKey) {
        return { installationId, team, apiKey: json.apiKey, callbackKey: json.callbackKey };
      }
      toast.error("Failed to rotate keys");
      return null;
    } catch {
      toast.error("Failed to rotate keys");
      return null;
    } finally {
      set({ rotatingId: null });
    }
  },
}));
